/**
 * Utility functions for working with Google Places API data
 */

import { City } from "../models/City";

/**
 * Converts a Google Places PlaceResult to a formatted city label
 * Format: "City, Region, Country" (avoiding duplicates)
 *
 * @param place - Google Maps PlaceResult object
 * @returns Formatted city label string
 */
export const formatPlaceToCity = (
	place: google.maps.places.PlaceResult | null
): string => {
	let city = "";
	let region = "";
	let country = "";

	if (!place) {
		return "";
	}

	// Extract city, region, and country from address components
	if (place.address_components) {
		for (const component of place.address_components) {
			const componentType = component.types[0];

			switch (componentType) {
				case "locality":
					city = component.long_name;
					break;
				case "postal_town":
					if (!city) {
						city = component.long_name;
					}
					break;
				case "administrative_area_level_1":
					region = component.long_name;
					break;
				case "country":
					country = component.long_name;
					break;
			}
		}
	}

	// Create formatted label: "City, Region, Country"
	let label = city ?? place.name ?? "";
	if (region && region !== city) {
		label += `, ${region}`;
	}
	if (country && country !== region) {
		label += `, ${country}`;
	}

	return label;
};

/**
 * Converts a Google Places PlaceResult to a City object
 *
 * @param place - Google Maps PlaceResult object
 * @returns City object or null if place is invalid
 */
export const convertPlaceToCity = (
	place: google.maps.places.PlaceResult | null
): City | null => {
	if (!place) {
		return null;
	}

	return {
		id: place.place_id,
		label: formatPlaceToCity(place)
	} as City;
};
